// ============================================================================
// GUEST SERVICE
// ============================================================================
// Business logic and API calls for Guest module

import { get, post, put, del } from '../client/axiosClient';
import { guestEndpoints } from '../endpoints/guest.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Guest,
  CreateGuestRequest,
  UpdateGuestRequest,
  GuestSearchRequest,
} from '../types';

/**
 * Guest Service Class
 */
class GuestService {
  /**
   * Get all guests (paginated)
   */
  async getAll(params?: GuestSearchRequest): Promise<PaginatedResponse<Guest>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Guest', params)
      : guestEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get guest by ID
   */
  async getById(id: ID): Promise<ApiResponse<Guest>> {
    const response = await get<ApiResponse<Guest>>(guestEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get guest by email
   */
  async getByEmail(email: string): Promise<ApiResponse<Guest>> {
    const response = await get<ApiResponse<Guest>>(guestEndpoints.getByEmail(email));
    return response.data;
  }

  /**
   * Create new guest
   */
  async create(data: CreateGuestRequest): Promise<ApiResponse<Guest>> {
    const response = await post<ApiResponse<Guest>>(
      guestEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update guest
   */
  async update(id: ID, data: UpdateGuestRequest): Promise<ApiResponse<Guest>> {
    const response = await put<ApiResponse<Guest>>(
      guestEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete guest
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(guestEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  /**
   * Validate guest email uniqueness
   */
  async validateEmail(email: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = guestEndpoints.validateEmail(email);
    if (excludeId) {
      endpoint = `${endpoint}?excludeId=${excludeId}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const guestService = new GuestService();
export default guestService;
